import { useEffect, useState } from 'react';
import VedcoolMark from './VedcoolMark.js';
import styles from './SplashScreen.module.css';

/**
 * SplashScreen - animated brand intro shown on first load.
 *
 * The animation is entirely CSS (see SplashScreen.module.css). React's only
 * jobs here are (a) unmounting the overlay once the timeline has finished so it
 * cannot trap clicks, and (b) locking body scroll while it is visible.
 *
 * Running the motion in CSS rather than a JS timeline matters: the main thread
 * is busy hydrating the app underneath, and compositor-driven
 * transform/opacity animations keep playing smoothly through that work.
 *
 * Props:
 *   duration  ms before the overlay unmounts. Must stay >= the CSS timeline
 *             (~2.6s) or the overlay will vanish mid-animation.
 *   onDone    optional callback fired when the splash finishes.
 */

export default function SplashScreen({ duration = 2600, onDone }) {
  const [done, setDone] = useState(false);

  useEffect(() => {
    // Honour reduced-motion by cutting the splash short - the CSS collapses
    // the animation, so holding the overlay for the full duration would just
    // show a static logo for 2.6s.
    const reduced = window.matchMedia?.(
      '(prefers-reduced-motion: reduce)'
    )?.matches;
    const wait = reduced ? 1000 : duration;

    const timer = setTimeout(() => {
      setDone(true);
      onDone?.();
    }, wait);

    return () => clearTimeout(timer);
  }, [duration, onDone]);

  // Lock scroll for as long as the splash is up, then restore whatever the
  // document had before (rather than blindly clearing it).
  useEffect(() => {
    if (done) return undefined;

    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previous;
    };
  }, [done]);

  if (done) return null;

  return (
    <div className={styles.overlay} role="status" aria-live="polite">
      <span className="visually-hidden">Loading Vedcool</span>

      <div className={styles.stage} aria-hidden="true">
        <div className={styles.markWrap}>
          <span className={styles.pulse} />

          <VedcoolMark
            className={styles.mark}
            bodyClassName={styles.cover}
            spineFillClassName={styles.spineFill}
            spineStrokeClassName={styles.spineStroke}
            bookmarkClassName={styles.bookmark}
            eWipeClassName={styles.eWipe}
            withEWipe
          />

          <span className={styles.sheen} />
        </div>

        <span className={styles.bar} />
      </div>
    </div>
  );
}
